import type { ReactNode } from "react";
import { __ } from "@/i18n";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useWidgetStore } from "@/stores/widget-store";
import { WidgetPublish } from "@/components/widget-publish";
import { DEFAULT_WIDGET_CONFIG } from "@/constants/widget-config";
import { FontSizeIcon } from "@/icons/font-size";
import { HighlightTitleIcon } from "@/icons/highlight-title";
import { HighlightLinksIcon } from "@/icons/highlight-links";
import { DyslexiaFontIcon } from "@/icons/dyslexia-font";
import { FontWeightIcon } from "@/icons/font-weight";
import { LetterSpacingIcon } from "@/icons/letter-spacing";
import { LineHeightIcon } from "@/icons/line-height";
import { TextAlignIcon } from "@/icons/text-align";
import { DarkContrastIcon } from "@/icons/dark-contrast";
import { LightContrastIcon } from "@/icons/light-contrast";
import { HighContrastIcon } from "@/icons/high-contrast";
import { HighSaturationIcon } from "@/icons/high-saturation";
import { LowSaturationIcon } from "@/icons/low-saturation";
import { MonochromeIcon } from "@/icons/monochrome";
import { BigCursorIcon } from "@/icons/big-cursor";
import { StopAnimationsIcon } from "@/icons/stop-animations";
import { ReadingGuideIcon } from "@/icons/reading-guide";
import { PageReadIcon } from "@/icons/page-read";
import { MuteSoundsIcon } from "@/icons/mute-sounds";
import { Accessibility } from "@/icons/accessibility";

interface ModuleRowProps {
  id: string;
  label: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ModuleRow({ id, label, icon: Icon, checked, onChange }: ModuleRowProps) {
  return (
    <div className="cy:flex cy:items-center cy:justify-between cy:px-4 cy:py-5 cy:border-t">
      <div className="cy:flex cy:items-center cy:gap-3">
        {Icon ? (
          <Icon width={20} height={20} className="cy:text-gray-500 cy:shrink-0" />
        ) : null}
        <label
          htmlFor={id}
          className="cy:text-sm! cy:font-normal! cy:text-gray-800! cy:cursor-pointer"
        >
          {label}
        </label>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

interface ModuleSectionProps {
  title: string;
  children: ReactNode;
}

/** Renders a labelled group of module toggles. Uses h3, intended to nest under the CardTitle h2. */
function ModuleSection({ title, children }: ModuleSectionProps) {
  return (
    <Card className="cy:rounded-lg cy:gap-0 cy:py-0 cy:shadow-none! cy:overflow-hidden">
      <div className="cy:bg-gray-50 cy:px-4 cy:py-3 cy:border-b">
        <h3 className="cy:text-sm cy:font-semibold cy:text-gray-900">{title}</h3>
      </div>
      <div>{children}</div>
    </Card>
  );
}

export const ModulesTab = () => {
  const { config, setModuleEnabled } = useWidgetStore();
  const color = config.modules?.color ?? DEFAULT_WIDGET_CONFIG.modules.color;
  const content = config.modules?.content ?? DEFAULT_WIDGET_CONFIG.modules.content;
  const navigation = config.modules?.navigation ?? DEFAULT_WIDGET_CONFIG.modules.navigation;
  const profiles = config.modules?.profiles ?? DEFAULT_WIDGET_CONFIG.modules.profiles;
  const pageRead = config.modules?.pageRead ?? DEFAULT_WIDGET_CONFIG.modules.pageRead;
  const muteSounds = config.modules?.muteSounds ?? DEFAULT_WIDGET_CONFIG.modules.muteSounds;

  return (
    <div className="cy:max-w-7xl cy:mx-auto cy:px-4 cy:sm:px-6 cy:lg:px-8 cy:pt-8">
      <Card className="cy:rounded-lg cy:gap-0 cy:shadow-none!">
        <CardHeader className="cy:border-b cy:pb-6">
          <CardTitle className="cy:text-xl">{__( 'Modules section', 'accessibility-widget' )}</CardTitle>
          <CardDescription>
            {__( 'Enable or disable individual accessibility features for your users.', 'accessibility-widget' )}
          </CardDescription>
          <CardAction>
            <WidgetPublish />
          </CardAction>
        </CardHeader>
        <CardContent className="cy:p-6">
          <div className="cy:flex cy:flex-col cy:gap-6">
        <ModuleSection title={__( 'Accessibility profile', 'accessibility-widget' )}>
          <ModuleRow
            id="module-profiles"
            label={__( 'Accessibility profile', 'accessibility-widget' )}
            icon={Accessibility}
            checked={profiles?.enabled ?? true}
            onChange={(v) => setModuleEnabled("profiles", "enabled", v)}
          />
        </ModuleSection>

        <ModuleSection title={__( 'Content adjustments', 'accessibility-widget' )}>
          <ModuleRow
            id="module-adjust-font-sizing"
            label={__( 'Adjust Font Sizing', 'accessibility-widget' )}
            icon={FontSizeIcon}
            checked={content.adjustFontSizing?.enabled ?? true}
            onChange={(v) => setModuleEnabled("content", "adjustFontSizing", v)}
          />
          <ModuleRow
            id="module-highlight-title"
            label={__( 'Highlight Title', 'accessibility-widget' )}
            icon={HighlightTitleIcon}
            checked={content.highlightTitle?.enabled ?? true}
            onChange={(v) => setModuleEnabled("content", "highlightTitle", v)}
          />
          <ModuleRow
            id="module-highlight-links"
            label={__( 'Highlight Links', 'accessibility-widget' )}
            icon={HighlightLinksIcon}
            checked={content.highlightLinks?.enabled ?? true}
            onChange={(v) => setModuleEnabled("content", "highlightLinks", v)}
          />
          <ModuleRow
            id="module-dyslexic-font"
            label={__( 'Dyslexia Friendly Font', 'accessibility-widget' )}
            icon={DyslexiaFontIcon}
            checked={content.dyslexicFont?.enabled ?? true}
            onChange={(v) => setModuleEnabled("content", "dyslexicFont", v)}
          />
          <ModuleRow
            id="module-font-weight"
            label={__( 'Font Weight', 'accessibility-widget' )}
            icon={FontWeightIcon}
            checked={content.fontWeight?.enabled ?? true}
            onChange={(v) => setModuleEnabled("content", "fontWeight", v)}
          />
          <ModuleRow
            id="module-letter-spacing"
            label={__( 'Letter Spacing', 'accessibility-widget' )}
            icon={LetterSpacingIcon}
            checked={content.letterSpacing?.enabled ?? true}
            onChange={(v) => setModuleEnabled("content", "letterSpacing", v)}
          />
          <ModuleRow
            id="module-line-height"
            label={__( 'Line Height', 'accessibility-widget' )}
            icon={LineHeightIcon}
            checked={content.lineHeight?.enabled ?? true}
            onChange={(v) => setModuleEnabled("content", "lineHeight", v)}
          />
          <ModuleRow
            id="module-text-align"
            label={__( 'Text Align', 'accessibility-widget' )}
            icon={TextAlignIcon}
            checked={content.textAlign?.enabled ?? true}
            onChange={(v) => setModuleEnabled("content", "textAlign", v)}
          />
        </ModuleSection>

        <ModuleSection title={__( 'Colour adjustments', 'accessibility-widget' )}>
          <ModuleRow
            id="module-dark-contrast"
            label={__( 'Dark Contrast', 'accessibility-widget' )}
            icon={DarkContrastIcon}
            checked={color.darkContrast?.enabled ?? true}
            onChange={(v) => setModuleEnabled("color", "darkContrast", v)}
          />
          <ModuleRow
            id="module-light-contrast"
            label={__( 'Light Contrast', 'accessibility-widget' )}
            icon={LightContrastIcon}
            checked={color.lightContrast?.enabled ?? true}
            onChange={(v) => setModuleEnabled("color", "lightContrast", v)}
          />
          <ModuleRow
            id="module-high-contrast"
            label={__( 'High Contrast', 'accessibility-widget' )}
            icon={HighContrastIcon}
            checked={color.highContrast?.enabled ?? true}
            onChange={(v) => setModuleEnabled("color", "highContrast", v)}
          />
          <ModuleRow
            id="module-high-saturation"
            label={__( 'High Saturation', 'accessibility-widget' )}
            icon={HighSaturationIcon}
            checked={color.highSaturation?.enabled ?? true}
            onChange={(v) => setModuleEnabled("color", "highSaturation", v)}
          />
          <ModuleRow
            id="module-low-saturation"
            label={__( 'Low Saturation', 'accessibility-widget' )}
            icon={LowSaturationIcon}
            checked={color.lowSaturation?.enabled ?? true}
            onChange={(v) => setModuleEnabled("color", "lowSaturation", v)}
          />
          <ModuleRow
            id="module-monochrome"
            label={__( 'Monochrome', 'accessibility-widget' )}
            icon={MonochromeIcon}
            checked={color.monochrome?.enabled ?? true}
            onChange={(v) => setModuleEnabled("color", "monochrome", v)}
          />
        </ModuleSection>

        <ModuleSection title={__( 'Navigation adjustments', 'accessibility-widget' )}>
          <ModuleRow
            id="module-reading-guide"
            label={__( 'Reading Guide', 'accessibility-widget' )}
            icon={ReadingGuideIcon}
            checked={navigation?.readingGuide?.enabled ?? true}
            onChange={(v) => setModuleEnabled("navigation", "readingGuide", v)}
          />
          <ModuleRow
            id="module-pause-animations"
            label={__( 'Pause Animations', 'accessibility-widget' )}
            icon={StopAnimationsIcon}
            checked={navigation?.pauseAnimations?.enabled ?? true}
            onChange={(v) => setModuleEnabled("navigation", "pauseAnimations", v)}
          />
          <ModuleRow
            id="module-big-cursor"
            label={__( 'Big Cursor', 'accessibility-widget' )}
            icon={BigCursorIcon}
            checked={navigation?.bigCursor?.enabled ?? true}
            onChange={(v) => setModuleEnabled("navigation", "bigCursor", v)}
          />
          <ModuleRow
            id="module-page-read"
            label={__( 'Page read', 'accessibility-widget' )}
            icon={PageReadIcon}
            checked={pageRead?.enabled ?? true}
            onChange={(v) => setModuleEnabled("pageRead", "enabled", v)}
          />
          <ModuleRow
            id="module-mute-sounds"
            label={__( 'Mute sounds', 'accessibility-widget' )}
            icon={MuteSoundsIcon}
            checked={muteSounds?.enabled ?? true}
            onChange={(v) => setModuleEnabled("muteSounds", "enabled", v)}
          />
        </ModuleSection>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
